/**
 * Cliente API de la conciliación de accesos wifi contra eWeLink (F5).
 *
 * Solo lectura por ahora: las escrituras (activar, dar de baja, eliminar
 * de eWeLink) llegan en la fase siguiente. El backend informa `simulado`
 * y `puede_operar`, y la UI DEBE respetarlos — un botón "Activar" que no
 * activa es peor que uno deshabilitado.
 */
import { apiFetch } from './client';

export type EstadoWifiApi =
  | 'sin_solicitar'
  | 'en_validacion'
  | 'por_activar'
  | 'activo'
  | 'suspendido'
  | 'desactivado'
  | 'acceso_perdido'
  | 'error';

export interface FilaAccesoWifiRead {
  acceso_id: string;
  lote_identificador: string;
  residente_nombre: string;
  parentesco: string | null;
  email: string;
  estado: EstadoWifiApi;
  /** Alta en eWeLink: el `shareTime` más antiguo de los canales. */
  alta_ewelink_at: string | null;
  error: string | null;
  /** Etiqueta con la que figura en la app de eWeLink (`casa-iniciales`). */
  comment: string | null;
  /** False si los canales no comparten la misma etiqueta. eWeLink la guarda
   *  POR DISPOSITIVO, así que editarla a mano en uno no la cambia en los
   *  otros y el vecino queda con dos. */
  comment_consistente: boolean;
  comments_en_ewelink: string[];
  /** Correo del residente hoy; difiere de `email` si cambió. */
  email_actual: string | null;
}

export interface FilaHuerfanoWifiRead {
  email: string;
  /** Necesario para revocar: el DELETE de eWeLink es por apikey, no por
   *  correo. */
  apikey: string;
  comentario: string | null;
  compartido_desde: string | null;
  /** Para saber a quién avisarle; no cambia la acción (el portal manda). */
  lote_identificador: string | null;
  nombre_padron: string | null;
}

export interface FilaOcupacionRead {
  deviceid: string;
  nombre: string;
  es_virtual: boolean;
  usados: number;
  limite: number;
  /** Agrupa el físico con su virtual de desborde. */
  canal: string;
}

/** Correo compartido en algunos canales, no en todos (2026-10-06). */
export interface FilaIncompletoRead {
  email: string;
  canales_presentes: string[];
  canales_faltantes: string[];
  comentario: string | null;
  /** Solo con ticket vigente y activo: habilita "Completar". */
  acceso_id: string | null;
  lote_identificador: string | null;
  nombre: string | null;
  /** Por qué no se ofrece Completar (null = sí se ofrece). */
  motivo_sin_accion: 'sin_ticket' | 'correo_cambiado' | 'suspendido' | null;
}

export interface ConciliacionWifiRead {
  por_activar: FilaAccesoWifiRead[];
  por_dar_de_baja: FilaAccesoWifiRead[];
  activos: FilaAccesoWifiRead[];
  huerfanos: FilaHuerfanoWifiRead[];
  sin_acceso_real: FilaAccesoWifiRead[];
  /** Compartidos con un correo que ya no es del residente. */
  correo_cambiado: FilaAccesoWifiRead[];
  /** En algunos canales, no en todos. */
  incompletos: FilaIncompletoRead[];
  /** canal → nombre del dispositivo físico, como lo configuró el barrio. */
  nombres_canal: Record<string, string>;
  ocupacion: FilaOcupacionRead[];
  conexion: { ok: boolean; mensaje: string; consultado_at: string | null };
  /** El backend está en modo simulación: nada se ejecuta. */
  simulado: boolean;
  /** Tesorero ejecuta; comisión solo mira. */
  puede_operar: boolean;
}

export function getConciliacionWifi(): Promise<ConciliacionWifiRead> {
  return apiFetch<ConciliacionWifiRead>('/accesos-wifi/conciliacion');
}

// === Ejecución contra eWeLink ===
//
// Solo tesorería. El backend decide por rol (`puede_operar` en la
// conciliación); acá solo se llama.

export interface ResultadoOperacion {
  ok: boolean;
  /** El backend está en modo `simulacion`: la operación NO se ejecutó. */
  simulado: boolean;
  mensaje: string;
  dispositivos: string[];
}

/** Comparte el acceso en los cuatro canales administrados. */
export function activarAccesoWifi(
  accesoId: string,
): Promise<ResultadoOperacion> {
  return apiFetch<ResultadoOperacion>(
    `/accesos-wifi/${accesoId}/activar`,
    { method: 'POST' },
  );
}

/** Comparte un acceso activo en los canales donde le falta. */
export function completarAccesoWifi(
  accesoId: string,
): Promise<ResultadoOperacion> {
  return apiFetch<ResultadoOperacion>(
    `/accesos-wifi/${accesoId}/completar`,
    { method: 'POST' },
  );
}

/** Comparte de nuevo en todos los canales un acceso que alguien quitó por
 *  fuera del portal (lista "sin acceso real"). */
export function volverACompartirAccesoWifi(
  accesoId: string,
): Promise<ResultadoOperacion> {
  return apiFetch<ResultadoOperacion>(
    `/accesos-wifi/${accesoId}/volver-a-compartir`,
    { method: 'POST' },
  );
}

/** Quita el acceso de todos los canales y deja auditoría. */
export function revocarAccesoWifi(
  accesoId: string,
  motivo: string,
): Promise<ResultadoOperacion> {
  return apiFetch<ResultadoOperacion>(
    `/accesos-wifi/${accesoId}/revocar`,
    { method: 'POST', body: JSON.stringify({ motivo }) },
  );
}

/** Revoca un acceso que existe en eWeLink sin ticket en el portal. */
export function revocarSinTicket(
  email: string,
  apikey: string,
  motivo: string,
): Promise<ResultadoOperacion> {
  return apiFetch<ResultadoOperacion>('/accesos-wifi/sin-ticket/revocar', {
    method: 'POST',
    body: JSON.stringify({ email, apikey, motivo }),
  });
}

/**
 * Quita el acceso del correo viejo y lo comparte con el nuevo, en ese orden.
 *
 * El orden importa: compartir primero dejaría, si la revocación falla, DOS
 * correos abriendo el portón — y el viejo puede ya no pertenecer al residente.
 */
export function reprogramarCorreo(
  accesoId: string,
): Promise<ResultadoOperacion> {
  return apiFetch<ResultadoOperacion>(
    `/accesos-wifi/${accesoId}/reprogramar-correo`,
    { method: 'POST' },
  );
}
